/**
 * Discord Tutorial Page
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const translations: Record<Locale, TutorialData> = {
  en: {
    pageTitle: 'Using Sokuji with Discord',
    backLink: 'Back to Supported Sites',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Overview',
      content: 'This guide will walk you through setting up and using Sokuji with Discord (Web version) for real-time language translation during your voice calls.',
    },
    steps: [
      {
        title: 'Install Sokuji Extension',
        content: 'Make sure you have the Sokuji extension installed in your Chrome or Edge browser. You can get it from the Chrome Web Store.',
        tip: 'The extension icon should appear in your browser toolbar after installation.',
      },
      {
        title: 'Open Discord Web',
        content: 'Navigate to <code>discord.com/app</code> or <code>discord.com/channels</code> and sign in with your Discord account.',
        tip: 'Sokuji works with the web version of Discord. The desktop app is not supported.',
      },
      {
        title: 'Join a Voice Channel',
        content: 'Join a voice channel or start a voice call with friends as you normally would.',
        tip: 'You can also use Sokuji in private calls and group DMs.',
      },
      {
        title: 'Configure Voice Settings',
        content: 'Click on the gear icon next to your username at the bottom to open User Settings. Go to Voice & Video settings, and under Input Device, select <strong>Sokuji Virtual Microphone</strong>.',
        tip: 'If the virtual microphone doesn\'t appear, refresh the page and ensure the Sokuji extension is active.',
      },
      {
        title: 'Start Speaking',
        content: 'Return to your voice channel. Your speech will now be translated in real-time as you speak.',
        tip: 'Speak at a natural pace for the best translation quality.',
      },
    ],
    tips: {
      title: 'Tips for Best Results',
      items: [
        'Use Push to Talk if there\'s background noise in your environment',
        'Test your setup in a private channel before important calls',
        'Keep sentences concise for more accurate translations',
      ],
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        {
          question: 'Does Sokuji work with the Discord desktop app?',
          answer: 'Currently, Sokuji only works with the web version of Discord accessed through supported browsers (Chrome, Edge).',
        },
        {
          question: 'Can I use Sokuji in Discord Stage channels?',
          answer: 'Yes, Sokuji works in Stage channels, voice channels, and private calls.',
        },
        {
          question: 'Will Sokuji translate text messages?',
          answer: 'No, Sokuji only translates voice audio. It does not translate text messages in chat.',
        },
      ],
    },
    troubleshooting: {
      title: 'Troubleshooting',
      content: 'If you encounter any issues, please visit our <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub repository</a> or contact us at <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> for support.',
    },
  },
  zh: {
    pageTitle: '在 Discord 中使用 Sokuji',
    backLink: '返回支持的网站',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概述',
      content: '本指南将指导您如何在 Discord（网页版）中设置和使用 Sokuji，实现语音通话中的实时语言翻译。',
    },
    steps: [
      {
        title: '安装 Sokuji 扩展',
        content: '确保您已在 Chrome 或 Edge 浏览器中安装了 Sokuji 扩展。您可以从 Chrome 网上应用店获取。',
        tip: '安装后，扩展图标应出现在浏览器工具栏中。',
      },
      {
        title: '打开 Discord 网页版',
        content: '访问 <code>discord.com/app</code> 或 <code>discord.com/channels</code> 并使用您的 Discord 账号登录。',
        tip: 'Sokuji 适用于 Discord 的网页版本。不支持桌面应用。',
      },
      {
        title: '加入语音频道',
        content: '像往常一样加入语音频道或与朋友开始语音通话。',
        tip: '您也可以在私人通话和群组私信中使用 Sokuji。',
      },
      {
        title: '配置语音设置',
        content: '点击底部用户名旁边的齿轮图标打开用户设置。进入语音和视频设置，在输入设备下选择 <strong>Sokuji Virtual Microphone</strong>。',
        tip: '如果虚拟麦克风没有出现，请刷新页面并确保 Sokuji 扩展处于活动状态。',
      },
      {
        title: '开始说话',
        content: '返回您的语音频道。现在您说话时，您的语音将被实时翻译。',
        tip: '以自然的速度说话以获得最佳翻译质量。',
      },
    ],
    tips: {
      title: '最佳使用建议',
      items: [
        '如果环境中有背景噪音，请使用按键说话功能',
        '在重要通话前在私人频道中测试您的设置',
        '保持句子简洁以获得更准确的翻译',
      ],
    },
    faq: {
      title: '常见问题',
      items: [
        {
          question: 'Sokuji 是否适用于 Discord 桌面应用？',
          answer: '目前，Sokuji 仅适用于通过支持的浏览器（Chrome、Edge）访问的 Discord 网页版。',
        },
        {
          question: '我可以在 Discord 舞台频道中使用 Sokuji 吗？',
          answer: '是的，Sokuji 可以在舞台频道、语音频道和私人通话中使用。',
        },
        {
          question: 'Sokuji 会翻译文字消息吗？',
          answer: '不会，Sokuji 只翻译语音音频。它不会翻译聊天中的文字消息。',
        },
      ],
    },
    troubleshooting: {
      title: '故障排除',
      content: '如果遇到任何问题，请访问我们的 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 仓库</a> 或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> 获取支持。',
    },
  },
  ja: {
    pageTitle: 'Discord で Sokuji を使用する',
    backLink: 'サポートされているサイトに戻る',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概要',
      content: 'このガイドでは、Discord（Web版）で Sokuji を設定して使用し、ボイスチャット中にリアルタイム言語翻訳を行う方法を説明します。',
    },
    steps: [
      {
        title: 'Sokuji 拡張機能をインストール',
        content: 'Chrome または Edge ブラウザに Sokuji 拡張機能がインストールされていることを確認してください。Chrome ウェブストアから入手できます。',
        tip: 'インストール後、拡張機能のアイコンがブラウザのツールバーに表示されます。',
      },
      {
        title: 'Discord Web を開く',
        content: '<code>discord.com/app</code> または <code>discord.com/channels</code> にアクセスし、Discord アカウントでサインインします。',
        tip: 'Sokuji は Discord の Web バージョンで動作します。デスクトップアプリはサポートされていません。',
      },
      {
        title: 'ボイスチャンネルに参加',
        content: '通常どおりボイスチャンネルに参加するか、友達とボイス通話を開始します。',
        tip: 'プライベート通話やグループDMでも Sokuji を使用できます。',
      },
      {
        title: 'ボイス設定を構成',
        content: '下部のユーザー名の横にある歯車アイコンをクリックしてユーザー設定を開きます。音声・ビデオ設定に移動し、入力デバイスで <strong>Sokuji Virtual Microphone</strong> を選択します。',
        tip: '仮想マイクが表示されない場合は、ページを更新し、Sokuji 拡張機能がアクティブであることを確認してください。',
      },
      {
        title: '話し始める',
        content: 'ボイスチャンネルに戻ります。話すと音声がリアルタイムで翻訳されるようになります。',
        tip: '最適な翻訳品質のために自然なペースで話してください。',
      },
    ],
    tips: {
      title: '最良の結果を得るためのヒント',
      items: [
        '環境に背景ノイズがある場合はプッシュトゥトークを使用する',
        '重要な通話の前にプライベートチャンネルで設定をテストする',
        'より正確な翻訳のために文を簡潔に保つ',
      ],
    },
    faq: {
      title: 'よくある質問',
      items: [
        {
          question: 'Sokuji は Discord デスクトップアプリで動作しますか？',
          answer: '現在、Sokuji はサポートされているブラウザ（Chrome、Edge）でアクセスする Discord の Web バージョンでのみ動作します。',
        },
        {
          question: 'Discord ステージチャンネルで Sokuji を使用できますか？',
          answer: 'はい、Sokuji はステージチャンネル、ボイスチャンネル、プライベート通話で動作します。',
        },
        {
          question: 'Sokuji はテキストメッセージを翻訳しますか？',
          answer: 'いいえ、Sokuji は音声オーディオのみを翻訳します。チャットのテキストメッセージは翻訳しません。',
        },
      ],
    },
    troubleshooting: {
      title: 'トラブルシューティング',
      content: '問題が発生した場合は、<a href="https://github.com/kizuna-ai-lab/sokuji">GitHub リポジトリ</a>を訪問するか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までメールでお問い合わせください。',
    },
  },
  ko: {
    pageTitle: 'Discord에서 Sokuji 사용하기',
    backLink: '지원 사이트로 돌아가기',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '개요',
      content: '이 가이드는 Discord(웹 버전)에서 Sokuji를 설정하고 사용하여 음성 통화 중 실시간 언어 번역을 수행하는 방법을 안내합니다.',
    },
    steps: [
      {
        title: 'Sokuji 확장 프로그램 설치',
        content: 'Chrome 또는 Edge 브라우저에 Sokuji 확장 프로그램이 설치되어 있는지 확인하세요. Chrome 웹 스토어에서 받을 수 있습니다.',
        tip: '설치 후 확장 프로그램 아이콘이 브라우저 도구 모음에 나타납니다.',
      },
      {
        title: 'Discord 웹 열기',
        content: '<code>discord.com/app</code> 또는 <code>discord.com/channels</code>로 이동하여 Discord 계정으로 로그인하세요.',
        tip: 'Sokuji는 Discord의 웹 버전에서 작동합니다. 데스크톱 앱은 지원되지 않습니다.',
      },
      {
        title: '음성 채널 참여',
        content: '평소처럼 음성 채널에 참여하거나 친구와 음성 통화를 시작하세요.',
        tip: '개인 통화와 그룹 DM에서도 Sokuji를 사용할 수 있습니다.',
      },
      {
        title: '음성 설정 구성',
        content: '하단의 사용자 이름 옆 톱니바퀴 아이콘을 클릭하여 사용자 설정을 엽니다. 음성 및 비디오 설정으로 이동하여 입력 장치에서 <strong>Sokuji Virtual Microphone</strong>을 선택하세요.',
        tip: '가상 마이크가 나타나지 않으면 페이지를 새로 고치고 Sokuji 확장 프로그램이 활성화되어 있는지 확인하세요.',
      },
      {
        title: '말하기 시작',
        content: '음성 채널로 돌아갑니다. 이제 말할 때 음성이 실시간으로 번역됩니다.',
        tip: '최적의 번역 품질을 위해 자연스러운 속도로 말하세요.',
      },
    ],
    tips: {
      title: '최상의 결과를 위한 팁',
      items: [
        '환경에 배경 소음이 있는 경우 푸시 투 토크 사용',
        '중요한 통화 전에 비공개 채널에서 설정 테스트',
        '더 정확한 번역을 위해 문장을 간결하게 유지',
      ],
    },
    faq: {
      title: '자주 묻는 질문',
      items: [
        {
          question: 'Sokuji가 Discord 데스크톱 앱에서 작동하나요?',
          answer: '현재 Sokuji는 지원되는 브라우저(Chrome, Edge)를 통해 액세스하는 Discord 웹 버전에서만 작동합니다.',
        },
        {
          question: 'Discord 스테이지 채널에서 Sokuji를 사용할 수 있나요?',
          answer: '네, Sokuji는 스테이지 채널, 음성 채널, 개인 통화에서 작동합니다.',
        },
        {
          question: 'Sokuji가 텍스트 메시지를 번역하나요?',
          answer: '아니요, Sokuji는 음성 오디오만 번역합니다. 채팅의 텍스트 메시지는 번역하지 않습니다.',
        },
      ],
    },
    troubleshooting: {
      title: '문제 해결',
      content: '문제가 발생하면 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 저장소</a>를 방문하거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 지원을 받으세요.',
    },
  },
  es: {
    pageTitle: 'Usar Sokuji con Discord',
    backLink: 'Volver a sitios compatibles',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Descripción general',
      content: 'Esta guía le explicará cómo configurar y usar Sokuji con Discord (versión web) para la traducción de idiomas en tiempo real durante sus llamadas de voz.',
    },
    steps: [
      {
        title: 'Instalar la extensión Sokuji',
        content: 'Asegúrese de tener la extensión Sokuji instalada en su navegador Chrome o Edge. Puede obtenerla en la Chrome Web Store.',
        tip: 'El icono de la extensión debería aparecer en la barra de herramientas de su navegador después de la instalación.',
      },
      {
        title: 'Abrir Discord Web',
        content: 'Navegue a <code>discord.com/app</code> o <code>discord.com/channels</code> e inicie sesión con su cuenta de Discord.',
        tip: 'Sokuji funciona con la versión web de Discord. La aplicación de escritorio no es compatible.',
      },
      {
        title: 'Unirse a un canal de voz',
        content: 'Únase a un canal de voz o inicie una llamada de voz con amigos como lo haría normalmente.',
        tip: 'También puede usar Sokuji en llamadas privadas y mensajes directos grupales.',
      },
      {
        title: 'Configurar los ajustes de voz',
        content: 'Haga clic en el icono de engranaje junto a su nombre de usuario en la parte inferior para abrir la Configuración de usuario. Vaya a Ajustes de Voz y Video, y en Dispositivo de entrada, seleccione <strong>Sokuji Virtual Microphone</strong>.',
        tip: 'Si el micrófono virtual no aparece, actualice la página y asegúrese de que la extensión Sokuji esté activa.',
      },
      {
        title: 'Empezar a hablar',
        content: 'Regrese a su canal de voz. Su voz ahora será traducida en tiempo real mientras habla.',
        tip: 'Hable a un ritmo natural para obtener la mejor calidad de traducción.',
      },
    ],
    tips: {
      title: 'Consejos para mejores resultados',
      items: [
        'Use Pulsar para Hablar si hay ruido de fondo en su entorno',
        'Pruebe su configuración en un canal privado antes de llamadas importantes',
        'Mantenga las oraciones concisas para traducciones más precisas',
      ],
    },
    faq: {
      title: 'Preguntas frecuentes',
      items: [
        {
          question: '¿Sokuji funciona con la aplicación de escritorio de Discord?',
          answer: 'Actualmente, Sokuji solo funciona con la versión web de Discord a través de navegadores compatibles (Chrome, Edge).',
        },
        {
          question: '¿Puedo usar Sokuji en los canales Stage de Discord?',
          answer: 'Sí, Sokuji funciona en canales Stage, canales de voz y llamadas privadas.',
        },
        {
          question: '¿Sokuji traducirá los mensajes de texto?',
          answer: 'No, Sokuji solo traduce el audio de voz. No traduce los mensajes de texto del chat.',
        },
      ],
    },
    troubleshooting: {
      title: 'Solución de problemas',
      content: 'Si encuentra algún problema, visite nuestro <a href="https://github.com/kizuna-ai-lab/sokuji">repositorio de GitHub</a> o contáctenos en <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obtener ayuda.',
    },
  },
  fr: {
    pageTitle: 'Utiliser Sokuji avec Discord',
    backLink: 'Retour aux sites pris en charge',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Aperçu',
      content: 'Ce guide vous expliquera comment configurer et utiliser Sokuji avec Discord (version web) pour la traduction linguistique en temps réel lors de vos appels vocaux.',
    },
    steps: [
      {
        title: 'Installer l\'extension Sokuji',
        content: 'Assurez-vous que l\'extension Sokuji est installée dans votre navigateur Chrome ou Edge. Vous pouvez l\'obtenir sur le Chrome Web Store.',
        tip: 'L\'icône de l\'extension devrait apparaître dans la barre d\'outils de votre navigateur après l\'installation.',
      },
      {
        title: 'Ouvrir Discord Web',
        content: 'Accédez à <code>discord.com/app</code> ou <code>discord.com/channels</code> et connectez-vous avec votre compte Discord.',
        tip: 'Sokuji fonctionne avec la version web de Discord. L\'application de bureau n\'est pas prise en charge.',
      },
      {
        title: 'Rejoindre un salon vocal',
        content: 'Rejoignez un salon vocal ou démarrez un appel vocal avec des amis comme vous le feriez normalement.',
        tip: 'Vous pouvez également utiliser Sokuji dans les appels privés et les messages directs de groupe.',
      },
      {
        title: 'Configurer les paramètres vocaux',
        content: 'Cliquez sur l\'icône d\'engrenage à côté de votre nom d\'utilisateur en bas pour ouvrir les Paramètres utilisateur. Allez dans les paramètres Voix et Vidéo, et sous Périphérique d\'entrée, sélectionnez <strong>Sokuji Virtual Microphone</strong>.',
        tip: 'Si le microphone virtuel n\'apparaît pas, actualisez la page et assurez-vous que l\'extension Sokuji est active.',
      },
      {
        title: 'Commencer à parler',
        content: 'Retournez dans votre salon vocal. Votre voix sera maintenant traduite en temps réel pendant que vous parlez.',
        tip: 'Parlez à un rythme naturel pour une qualité de traduction optimale.',
      },
    ],
    tips: {
      title: 'Conseils pour de meilleurs résultats',
      items: [
        'Utilisez Appuyer pour Parler s\'il y a du bruit de fond dans votre environnement',
        'Testez votre configuration dans un salon privé avant les appels importants',
        'Gardez les phrases concises pour des traductions plus précises',
      ],
    },
    faq: {
      title: 'Questions fréquemment posées',
      items: [
        {
          question: 'Sokuji fonctionne-t-il avec l\'application de bureau Discord ?',
          answer: 'Actuellement, Sokuji ne fonctionne qu\'avec la version web de Discord accessible via les navigateurs pris en charge (Chrome, Edge).',
        },
        {
          question: 'Puis-je utiliser Sokuji dans les salons Stage de Discord ?',
          answer: 'Oui, Sokuji fonctionne dans les salons Stage, les salons vocaux et les appels privés.',
        },
        {
          question: 'Sokuji traduira-t-il les messages textuels ?',
          answer: 'Non, Sokuji ne traduit que l\'audio vocal. Il ne traduit pas les messages textuels du chat.',
        },
      ],
    },
    troubleshooting: {
      title: 'Dépannage',
      content: 'Si vous rencontrez des problèmes, veuillez visiter notre <a href="https://github.com/kizuna-ai-lab/sokuji">dépôt GitHub</a> ou nous contacter à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> pour obtenir de l\'aide.',
    },
  },
  ar: {
    pageTitle: 'استخدام Sokuji مع Discord',
    backLink: 'العودة إلى المواقع المدعومة',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'نظرة عامة',
      content: 'سيرشدك هذا الدليل خلال إعداد واستخدام Sokuji مع Discord (نسخة الويب) للترجمة اللغوية الفورية أثناء مكالمات الصوت.',
    },
    steps: [
      {
        title: 'تثبيت إضافة Sokuji',
        content: 'تأكد من تثبيت إضافة Sokuji في متصفح Chrome أو Edge. يمكنك الحصول عليها من سوق Chrome الإلكتروني.',
        tip: 'يجب أن يظهر رمز الإضافة في شريط أدوات المتصفح بعد التثبيت.',
      },
      {
        title: 'فتح Discord على الويب',
        content: 'انتقل إلى <code>discord.com/app</code> أو <code>discord.com/channels</code> وقم بتسجيل الدخول بحساب Discord الخاص بك.',
        tip: 'يعمل Sokuji مع نسخة الويب من Discord. تطبيق سطح المكتب غير مدعوم.',
      },
      {
        title: 'الانضمام إلى قناة صوتية',
        content: 'انضم إلى قناة صوتية أو ابدأ مكالمة صوتية مع الأصدقاء كما تفعل عادةً.',
        tip: 'يمكنك أيضًا استخدام Sokuji في المكالمات الخاصة والرسائل المباشرة الجماعية.',
      },
      {
        title: 'تكوين إعدادات الصوت',
        content: 'انقر على أيقونة الترس بجانب اسم المستخدم في الأسفل لفتح إعدادات المستخدم. انتقل إلى إعدادات الصوت والفيديو، وتحت جهاز الإدخال، اختر <strong>Sokuji Virtual Microphone</strong>.',
        tip: 'إذا لم يظهر الميكروفون الافتراضي، قم بتحديث الصفحة وتأكد من أن إضافة Sokuji نشطة.',
      },
      {
        title: 'ابدأ بالتحدث',
        content: 'عد إلى قناتك الصوتية. سيتم الآن ترجمة كلامك في الوقت الفعلي أثناء تحدثك.',
        tip: 'تحدث بوتيرة طبيعية للحصول على أفضل جودة ترجمة.',
      },
    ],
    tips: {
      title: 'نصائح للحصول على أفضل النتائج',
      items: [
        'استخدم خاصية اضغط للتحدث إذا كان هناك ضوضاء خلفية في بيئتك',
        'اختبر إعداداتك في قناة خاصة قبل المكالمات المهمة',
        'حافظ على الجمل مختصرة لترجمات أكثر دقة',
      ],
    },
    faq: {
      title: 'الأسئلة الشائعة',
      items: [
        {
          question: 'هل يعمل Sokuji مع تطبيق Discord لسطح المكتب؟',
          answer: 'حاليًا، يعمل Sokuji فقط مع نسخة الويب من Discord عبر المتصفحات المدعومة (Chrome، Edge).',
        },
        {
          question: 'هل يمكنني استخدام Sokuji في قنوات Stage على Discord؟',
          answer: 'نعم، يعمل Sokuji في قنوات Stage والقنوات الصوتية والمكالمات الخاصة.',
        },
        {
          question: 'هل سيترجم Sokuji الرسائل النصية؟',
          answer: 'لا، يترجم Sokuji الصوت فقط. لا يترجم الرسائل النصية في الدردشة.',
        },
      ],
    },
    troubleshooting: {
      title: 'استكشاف الأخطاء وإصلاحها',
      content: 'إذا واجهت أي مشاكل، يرجى زيارة <a href="https://github.com/kizuna-ai-lab/sokuji">مستودع GitHub</a> الخاص بنا أو التواصل معنا عبر <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> للحصول على الدعم.',
    },
  },
  pt: {
    pageTitle: 'Usando o Sokuji com o Discord',
    backLink: 'Voltar para sites compatíveis',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Visão geral',
      content: 'Este guia vai orientá-lo na configuração e uso do Sokuji com o Discord (versão web) para tradução de idiomas em tempo real durante suas chamadas de voz.',
    },
    steps: [
      {
        title: 'Instalar a extensão Sokuji',
        content: 'Certifique-se de que a extensão Sokuji está instalada no seu navegador Chrome ou Edge. Você pode obtê-la na Chrome Web Store.',
        tip: 'O ícone da extensão deve aparecer na barra de ferramentas do seu navegador após a instalação.',
      },
      {
        title: 'Abrir o Discord Web',
        content: 'Navegue até <code>discord.com/app</code> ou <code>discord.com/channels</code> e faça login com sua conta do Discord.',
        tip: 'O Sokuji funciona com a versão web do Discord. O aplicativo de desktop não é compatível.',
      },
      {
        title: 'Entrar em um canal de voz',
        content: 'Entre em um canal de voz ou inicie uma chamada de voz com amigos como faria normalmente.',
        tip: 'Você também pode usar o Sokuji em chamadas privadas e mensagens diretas em grupo.',
      },
      {
        title: 'Configurar as definições de voz',
        content: 'Clique no ícone de engrenagem ao lado do seu nome de usuário na parte inferior para abrir as Configurações do Usuário. Vá para as configurações de Voz e Vídeo e, em Dispositivo de Entrada, selecione <strong>Sokuji Virtual Microphone</strong>.',
        tip: 'Se o microfone virtual não aparecer, atualize a página e certifique-se de que a extensão Sokuji está ativa.',
      },
      {
        title: 'Começar a falar',
        content: 'Volte ao seu canal de voz. Sua fala agora será traduzida em tempo real enquanto você fala.',
        tip: 'Fale em um ritmo natural para obter a melhor qualidade de tradução.',
      },
    ],
    tips: {
      title: 'Dicas para melhores resultados',
      items: [
        'Use Pressionar para Falar se houver ruído de fundo no seu ambiente',
        'Teste sua configuração em um canal privado antes de chamadas importantes',
        'Mantenha as frases concisas para traduções mais precisas',
      ],
    },
    faq: {
      title: 'Perguntas frequentes',
      items: [
        {
          question: 'O Sokuji funciona com o aplicativo de desktop do Discord?',
          answer: 'Atualmente, o Sokuji só funciona com a versão web do Discord acessada por navegadores compatíveis (Chrome, Edge).',
        },
        {
          question: 'Posso usar o Sokuji nos canais Stage do Discord?',
          answer: 'Sim, o Sokuji funciona em canais Stage, canais de voz e chamadas privadas.',
        },
        {
          question: 'O Sokuji traduzirá mensagens de texto?',
          answer: 'Não, o Sokuji traduz apenas o áudio de voz. Ele não traduz mensagens de texto no chat.',
        },
      ],
    },
    troubleshooting: {
      title: 'Solução de problemas',
      content: 'Se encontrar algum problema, visite nosso <a href="https://github.com/kizuna-ai-lab/sokuji">repositório no GitHub</a> ou entre em contato conosco em <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obter suporte.',
    },
  },
  ru: {
    pageTitle: 'Использование Sokuji с Discord',
    backLink: 'Назад к поддерживаемым сайтам',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Обзор',
      content: 'Это руководство поможет вам настроить и использовать Sokuji с Discord (веб-версия) для перевода в реальном времени во время голосовых звонков.',
    },
    steps: [
      {
        title: 'Установите расширение Sokuji',
        content: 'Убедитесь, что расширение Sokuji установлено в вашем браузере Chrome или Edge. Вы можете получить его в Интернет-магазине Chrome.',
        tip: 'После установки значок расширения должен появиться на панели инструментов браузера.',
      },
      {
        title: 'Откройте Discord Web',
        content: 'Перейдите на <code>discord.com/app</code> или <code>discord.com/channels</code> и войдите в свою учётную запись Discord.',
        tip: 'Sokuji работает с веб-версией Discord. Настольное приложение не поддерживается.',
      },
      {
        title: 'Присоединитесь к голосовому каналу',
        content: 'Присоединитесь к голосовому каналу или начните голосовой звонок с друзьями как обычно.',
        tip: 'Вы также можете использовать Sokuji в личных звонках и групповых сообщениях.',
      },
      {
        title: 'Настройте параметры голоса',
        content: 'Нажмите на значок шестерёнки рядом с вашим именем пользователя внизу, чтобы открыть Настройки пользователя. Перейдите в настройки Голос и Видео и в разделе Устройство ввода выберите <strong>Sokuji Virtual Microphone</strong>.',
        tip: 'Если виртуальный микрофон не отображается, обновите страницу и убедитесь, что расширение Sokuji активно.',
      },
      {
        title: 'Начните говорить',
        content: 'Вернитесь в голосовой канал. Теперь ваша речь будет переводиться в реальном времени.',
        tip: 'Говорите в естественном темпе для оптимального качества перевода.',
      },
    ],
    tips: {
      title: 'Советы для лучших результатов',
      items: [
        'Используйте режим «Нажми и говори», если в вашем окружении есть фоновый шум',
        'Протестируйте настройки в приватном канале перед важными звонками',
        'Формулируйте предложения кратко для более точного перевода',
      ],
    },
    faq: {
      title: 'Часто задаваемые вопросы',
      items: [
        {
          question: 'Работает ли Sokuji с настольным приложением Discord?',
          answer: 'В настоящее время Sokuji работает только с веб-версией Discord через поддерживаемые браузеры (Chrome, Edge).',
        },
        {
          question: 'Могу ли я использовать Sokuji в каналах Stage Discord?',
          answer: 'Да, Sokuji работает в каналах Stage, голосовых каналах и личных звонках.',
        },
        {
          question: 'Будет ли Sokuji переводить текстовые сообщения?',
          answer: 'Нет, Sokuji переводит только голосовой звук. Текстовые сообщения в чате не переводятся.',
        },
      ],
    },
    troubleshooting: {
      title: 'Устранение неполадок',
      content: 'Если вы столкнулись с проблемами, посетите наш <a href="https://github.com/kizuna-ai-lab/sokuji">репозиторий на GitHub</a> или свяжитесь с нами по адресу <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> для получения поддержки.',
    },
  },
  de: {
    pageTitle: 'Sokuji mit Discord verwenden',
    backLink: 'Zurück zu unterstützten Seiten',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Überblick',
      content: 'Diese Anleitung führt Sie durch die Einrichtung und Verwendung von Sokuji mit Discord (Webversion) für Echtzeit-Sprachübersetzung während Ihrer Sprachanrufe.',
    },
    steps: [
      {
        title: 'Sokuji-Erweiterung installieren',
        content: 'Stellen Sie sicher, dass die Sokuji-Erweiterung in Ihrem Chrome- oder Edge-Browser installiert ist. Sie können sie im Chrome Web Store erhalten.',
        tip: 'Das Symbol der Erweiterung sollte nach der Installation in der Symbolleiste Ihres Browsers erscheinen.',
      },
      {
        title: 'Discord Web öffnen',
        content: 'Navigieren Sie zu <code>discord.com/app</code> oder <code>discord.com/channels</code> und melden Sie sich mit Ihrem Discord-Konto an.',
        tip: 'Sokuji funktioniert mit der Webversion von Discord. Die Desktop-App wird nicht unterstützt.',
      },
      {
        title: 'Einem Sprachkanal beitreten',
        content: 'Treten Sie einem Sprachkanal bei oder starten Sie einen Sprachanruf mit Freunden wie gewohnt.',
        tip: 'Sie können Sokuji auch in privaten Anrufen und Gruppen-DMs verwenden.',
      },
      {
        title: 'Spracheinstellungen konfigurieren',
        content: 'Klicken Sie auf das Zahnradsymbol neben Ihrem Benutzernamen unten, um die Benutzereinstellungen zu öffnen. Gehen Sie zu Sprach- und Videoeinstellungen und wählen Sie unter Eingabegerät <strong>Sokuji Virtual Microphone</strong> aus.',
        tip: 'Wenn das virtuelle Mikrofon nicht angezeigt wird, aktualisieren Sie die Seite und stellen Sie sicher, dass die Sokuji-Erweiterung aktiv ist.',
      },
      {
        title: 'Sprechen Sie los',
        content: 'Kehren Sie zu Ihrem Sprachkanal zurück. Ihre Sprache wird jetzt in Echtzeit übersetzt, während Sie sprechen.',
        tip: 'Sprechen Sie in einem natürlichen Tempo für optimale Übersetzungsqualität.',
      },
    ],
    tips: {
      title: 'Tipps für beste Ergebnisse',
      items: [
        'Verwenden Sie Push-to-Talk, wenn es Hintergrundgeräusche in Ihrer Umgebung gibt',
        'Testen Sie Ihre Einrichtung in einem privaten Kanal vor wichtigen Anrufen',
        'Halten Sie Sätze kurz und prägnant für genauere Übersetzungen',
      ],
    },
    faq: {
      title: 'Häufig gestellte Fragen',
      items: [
        {
          question: 'Funktioniert Sokuji mit der Discord-Desktop-App?',
          answer: 'Derzeit funktioniert Sokuji nur mit der Webversion von Discord über unterstützte Browser (Chrome, Edge).',
        },
        {
          question: 'Kann ich Sokuji in Discord-Stage-Kanälen verwenden?',
          answer: 'Ja, Sokuji funktioniert in Stage-Kanälen, Sprachkanälen und privaten Anrufen.',
        },
        {
          question: 'Wird Sokuji Textnachrichten übersetzen?',
          answer: 'Nein, Sokuji übersetzt nur Sprachaudio. Textnachrichten im Chat werden nicht übersetzt.',
        },
      ],
    },
    troubleshooting: {
      title: 'Fehlerbehebung',
      content: 'Wenn Sie auf Probleme stoßen, besuchen Sie bitte unser <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub-Repository</a> oder kontaktieren Sie uns unter <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> für Unterstützung.',
    },
  },
  uk: {
    pageTitle: 'Використання Sokuji з Discord',
    backLink: 'Назад до підтримуваних сайтів',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Огляд',
      content: 'Цей посібник допоможе вам налаштувати та використовувати Sokuji з Discord (веб-версія) для перекладу мов у реальному часі під час голосових дзвінків.',
    },
    steps: [
      {
        title: 'Встановіть розширення Sokuji',
        content: 'Переконайтеся, що розширення Sokuji встановлено у вашому браузері Chrome або Edge. Ви можете отримати його в Інтернет-магазині Chrome.',
        tip: 'Після встановлення значок розширення повинен з\'явитися на панелі інструментів браузера.',
      },
      {
        title: 'Відкрийте Discord Web',
        content: 'Перейдіть на <code>discord.com/app</code> або <code>discord.com/channels</code> та увійдіть у свій обліковий запис Discord.',
        tip: 'Sokuji працює з веб-версією Discord. Настільний додаток не підтримується.',
      },
      {
        title: 'Приєднайтеся до голосового каналу',
        content: 'Приєднайтеся до голосового каналу або розпочніть голосовий дзвінок з друзями як зазвичай.',
        tip: 'Ви також можете використовувати Sokuji в особистих дзвінках та групових повідомленнях.',
      },
      {
        title: 'Налаштуйте параметри голосу',
        content: 'Натисніть на значок шестірні поруч з вашим іменем користувача внизу, щоб відкрити Налаштування користувача. Перейдіть до налаштувань Голосу та Відео і в розділі Пристрій введення виберіть <strong>Sokuji Virtual Microphone</strong>.',
        tip: 'Якщо віртуальний мікрофон не з\'являється, оновіть сторінку та переконайтеся, що розширення Sokuji активне.',
      },
      {
        title: 'Почніть говорити',
        content: 'Поверніться до голосового каналу. Тепер ваше мовлення буде перекладатися в реальному часі.',
        tip: 'Говоріть у природному темпі для оптимальної якості перекладу.',
      },
    ],
    tips: {
      title: 'Поради для кращих результатів',
      items: [
        'Використовуйте режим «Натисни і говори», якщо у вашому оточенні є фоновий шум',
        'Протестуйте налаштування в приватному каналі перед важливими дзвінками',
        'Формулюйте речення стисло для точнішого перекладу',
      ],
    },
    faq: {
      title: 'Часті запитання',
      items: [
        {
          question: 'Чи працює Sokuji з настільним додатком Discord?',
          answer: 'Наразі Sokuji працює лише з веб-версією Discord через підтримувані браузери (Chrome, Edge).',
        },
        {
          question: 'Чи можу я використовувати Sokuji в каналах Stage Discord?',
          answer: 'Так, Sokuji працює в каналах Stage, голосових каналах та особистих дзвінках.',
        },
        {
          question: 'Чи перекладатиме Sokuji текстові повідомлення?',
          answer: 'Ні, Sokuji перекладає лише голосове аудіо. Текстові повідомлення в чаті не перекладаються.',
        },
      ],
    },
    troubleshooting: {
      title: 'Усунення несправностей',
      content: 'Якщо у вас виникли проблеми, відвідайте наш <a href="https://github.com/kizuna-ai-lab/sokuji">репозиторій на GitHub</a> або зв\'яжіться з нами за адресою <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> для отримання підтримки.',
    },
  },
  it: {
    pageTitle: 'Usare Sokuji con Discord',
    backLink: 'Torna ai siti supportati',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Panoramica',
      content: 'Questa guida ti spiegherà come configurare e utilizzare Sokuji con Discord (versione web) per la traduzione linguistica in tempo reale durante le tue chiamate vocali.',
    },
    steps: [
      {
        title: 'Installare l\'estensione Sokuji',
        content: 'Assicurati di avere l\'estensione Sokuji installata nel tuo browser Chrome o Edge. Puoi ottenerla dal Chrome Web Store.',
        tip: 'L\'icona dell\'estensione dovrebbe apparire nella barra degli strumenti del browser dopo l\'installazione.',
      },
      {
        title: 'Aprire Discord Web',
        content: 'Vai su <code>discord.com/app</code> o <code>discord.com/channels</code> e accedi con il tuo account Discord.',
        tip: 'Sokuji funziona con la versione web di Discord. L\'app desktop non è supportata.',
      },
      {
        title: 'Entrare in un canale vocale',
        content: 'Entra in un canale vocale o avvia una chiamata vocale con gli amici come faresti normalmente.',
        tip: 'Puoi anche usare Sokuji nelle chiamate private e nei messaggi diretti di gruppo.',
      },
      {
        title: 'Configurare le impostazioni vocali',
        content: 'Fai clic sull\'icona dell\'ingranaggio accanto al tuo nome utente in basso per aprire le Impostazioni utente. Vai alle impostazioni Voce e Video e, sotto Dispositivo di input, seleziona <strong>Sokuji Virtual Microphone</strong>.',
        tip: 'Se il microfono virtuale non appare, aggiorna la pagina e assicurati che l\'estensione Sokuji sia attiva.',
      },
      {
        title: 'Iniziare a parlare',
        content: 'Torna al tuo canale vocale. Il tuo discorso verrà ora tradotto in tempo reale mentre parli.',
        tip: 'Parla a un ritmo naturale per una qualità di traduzione ottimale.',
      },
    ],
    tips: {
      title: 'Consigli per i migliori risultati',
      items: [
        'Usa la modalità Premi per Parlare se c\'è rumore di fondo nel tuo ambiente',
        'Testa la tua configurazione in un canale privato prima di chiamate importanti',
        'Mantieni le frasi concise per traduzioni più accurate',
      ],
    },
    faq: {
      title: 'Domande frequenti',
      items: [
        {
          question: 'Sokuji funziona con l\'app desktop di Discord?',
          answer: 'Attualmente, Sokuji funziona solo con la versione web di Discord accessibile tramite browser supportati (Chrome, Edge).',
        },
        {
          question: 'Posso usare Sokuji nei canali Stage di Discord?',
          answer: 'Sì, Sokuji funziona nei canali Stage, nei canali vocali e nelle chiamate private.',
        },
        {
          question: 'Sokuji tradurrà i messaggi di testo?',
          answer: 'No, Sokuji traduce solo l\'audio vocale. Non traduce i messaggi di testo nella chat.',
        },
      ],
    },
    troubleshooting: {
      title: 'Risoluzione dei problemi',
      content: 'Se riscontri problemi, visita il nostro <a href="https://github.com/kizuna-ai-lab/sokuji">repository GitHub</a> o contattaci all\'indirizzo <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> per ricevere assistenza.',
    },
  },
};

export function DiscordTutorial() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/discord" />;
}
